/** Painel de crescimento estilo Search Console: filtros de período + cards
 *  de métrica clicáveis + gráfico multi-linha. Reusado pela Lara e pelo cliente. */
import { useState } from "react";
import {
  METRIC_DEFS, RANGES, filterByRange, latestValue, fmtValue,
  type Metric, type MetricKey,
} from "../lib/metrics";
import { MultiLineChart } from "./MultiLineChart";

type Group = "todos" | "instagram" | "negocio";
const GROUPS: [Group, string][] = [["todos", "Todos"], ["instagram", "Instagram"], ["negocio", "Negócio"]];

export function GrowthPanel({ metrics }: { metrics: Metric[] }) {
  const [rangeKey, setRangeKey] = useState("3m");
  const [group, setGroup] = useState<Group>("todos");
  const [active, setActive] = useState<Set<MetricKey>>(new Set<MetricKey>(["followers", "revenue"]));

  const days = RANGES.find((r) => r.key === rangeKey)?.days ?? null;
  const ranged = filterByRange(metrics, days);
  const defs = METRIC_DEFS.filter((d) => group === "todos" || d.group === group);
  const activeList = defs.filter((d) => active.has(d.key)).map((d) => d.key);

  function toggle(key: MetricKey) {
    setActive((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1.5">
          {RANGES.map((r) => (
            <button
              key={r.key} type="button" onClick={() => setRangeKey(r.key)}
              className={`type-caption rounded-full border px-3 py-1 transition-colors ${
                rangeKey === r.key
                  ? "border-accent bg-[var(--accent-soft)] text-text-cream"
                  : "border-transparent text-text-tertiary hover:text-text-secondary"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        <div className="flex gap-4">
          {GROUPS.map(([g, label]) => (
            <button
              key={g} type="button" onClick={() => setGroup(g)}
              className={`type-caption transition-colors ${
                group === g ? "text-gold" : "text-text-tertiary hover:text-text-secondary"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
        {defs.map((def) => {
          const v = latestValue(ranged, def.key);
          const on = active.has(def.key);
          return (
            <button
              key={def.key} type="button" onClick={() => toggle(def.key)}
              className="rounded-[var(--radius-control)] border p-3 text-left transition-all"
              style={{
                borderColor: on ? def.color : "var(--border-subtle)",
                background: on ? "var(--surface-raised)" : "transparent",
              }}
            >
              <span className="flex items-center gap-1.5 type-caption text-text-tertiary">
                <span
                  className="h-1.5 w-1.5 shrink-0 rounded-full transition-opacity"
                  style={{ background: def.color, opacity: on ? 1 : 0.35 }}
                />
                {def.label}
              </span>
              <p className={`type-h3 mt-1 tabular-nums ${on ? "text-text-cream" : "text-text-tertiary"}`}>
                {v === null ? "—" : fmtValue(def, v)}
              </p>
            </button>
          );
        })}
      </div>

      <div className="mt-4 rounded-[var(--radius-control-lg)] border border-border-subtle bg-surface-sunken p-3">
        <MultiLineChart metrics={ranged} active={activeList} defs={METRIC_DEFS} />
      </div>
    </div>
  );
}
